// Display-only currency conversion for viewer-facing surfaces (XT-126).
//
// This lives inside the pricing module because `README.md` forbids callers
// recalculating exchange-rate conversions outside it. It is NOT a pricing
// path: it restates an already-final total in a second currency for reading
// only. Nothing here is persisted, and no `PricingInput` is involved —
// anything holding one must go through `calculatePricing`.
//
// RATE ORIENTATION — the one thing to get right. A stored rate row
// `(source_currency = S, target_currency = T, exchange_rate = R)` means
// "R units of T per 1 unit of S", so converting S → T MULTIPLIES by R
// (EUR 100 x 1.16 = USD 116). The engine's `findExchangeRate` divides instead,
// but only because it converts *into* the base currency using the inverse
// row — see the comment at `engine.ts` `findExchangeRate`. Do not copy the
// divide.

import currency from 'currency.js';
import { roundMoneyAmount } from './engine';

// The only pair we convert for display. Kept as constants so the "EUR only"
// rule lives in one place rather than being re-tested at each render site.
export const DISPLAY_SOURCE_CURRENCY = 'EUR';
export const DISPLAY_TARGET_CURRENCY = 'USD';

export type DisplayCurrency =
  | typeof DISPLAY_SOURCE_CURRENCY
  | typeof DISPLAY_TARGET_CURRENCY;

export const isDisplayCurrency = (value: unknown): value is DisplayCurrency =>
  value === DISPLAY_SOURCE_CURRENCY || value === DISPLAY_TARGET_CURRENCY;

// A rate is usable only if it is a real positive number. A missing, zero, or
// negative rate must leave the amount alone — never silently fall back to 1:1,
// which would render a EUR figure under a USD symbol.
export const isUsableDisplayRate = (
  rate: number | null | undefined
): rate is number =>
  typeof rate === 'number' && Number.isFinite(rate) && rate > 0;

// Multiplies through currency.js rather than `*`: raw IEEE-754 multiplication
// on exactly this conversion produced real sub-cent drift in the payments
// path (see `itinerary-payments/service.ts`). Result is quantised to 2dp so
// each surface's own formatter decides how many decimals to show.
export const convertForDisplay = (amount: number, rate: number): number => {
  if (!Number.isFinite(amount) || !isUsableDisplayRate(rate)) return amount;
  return roundMoneyAmount(
    currency(amount, { precision: 4 }).multiply(rate).value
  );
};

export type DisplayMoneyInput = {
  amount: number;
  // The amount's own currency, straight off the payload. Nullable because
  // `itinerary_item.currency` is a nullable column.
  currency: string | null | undefined;
  // What the viewer asked to see.
  displayCurrency: DisplayCurrency;
  // Source → target rate, or null when none is available.
  rate: number | null | undefined;
};

export type DisplayMoney = {
  amount: number;
  currency: string | null | undefined;
};

// The single decision point for "may this amount be restated in USD?".
//
// Converts only a EUR amount, and only when USD was asked for and a usable
// rate exists. Anything else is returned verbatim in its own currency. That
// guard matters: an item's currency can be null or non-EUR, and the optionals
// price difference is a raw subtraction with no FX and no currency-equality
// check, so its figure is already unreliable for a mixed-currency optional —
// restating it under a second symbol would compound that, not fix it.
export const resolveDisplayMoney = ({
  amount,
  currency: sourceCurrency,
  displayCurrency,
  rate,
}: DisplayMoneyInput): DisplayMoney => {
  if (displayCurrency !== DISPLAY_TARGET_CURRENCY) {
    return { amount, currency: sourceCurrency };
  }
  if (sourceCurrency !== DISPLAY_SOURCE_CURRENCY) {
    return { amount, currency: sourceCurrency };
  }
  if (!isUsableDisplayRate(rate)) return { amount, currency: sourceCurrency };
  return {
    amount: convertForDisplay(amount, rate),
    currency: DISPLAY_TARGET_CURRENCY,
  };
};
